import React, { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { uploadShopVideo } from '../../apis/atlantis.api';

const MAX_FILE_SIZE = 4.5 * 1024 * 1024; // 4.5MB — same ceiling as images
const TARGET_HINT = 'aim under 2 MB';

// Browsers/OSes report a valid .mp4 as video/mp4, application/mp4, or even
// application/octet-stream (drag-and-drop, mobile providers): trust the .mp4
// suffix as well as the MIME. The upload always declares video/mp4.
const MP4_MIME_TYPES = ['video/mp4', 'application/mp4'];
const isMp4 = file => MP4_MIME_TYPES.includes(file.type) || /\.mp4$/i.test(file.name || '');

// A real http(s) URL with a host — `https://?` or `http://` alone fail here.
const isHttpUrl = value => {
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && !!parsed.hostname;
  } catch {
    return false;
  }
};

// Shop-hour "Event Description Video" (DEGEN-1403). Mirrors ImageUploadField:
// pick or drop an MP4, it is base64-posted through the existing upload
// endpoint and the returned URL lands in the form. No conversion happens on
// our side — Admin exports the file small (H.264, no audio, fast start, under
// 2 MB). Save stays disabled while the upload runs (onBusyChange).
const VideoUploadField = ({ value, onChange, onBusyChange, token, label, id }) => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showManualUrl, setShowManualUrl] = useState(false);
  const [manualUrl, setManualUrl] = useState(value || '');
  const [manualUrlError, setManualUrlError] = useState('');
  // Last value this field handed to the form. When `value` changes from
  // elsewhere (the Edit modal filling formData after mount), mirror it into the
  // manual input; when it changes because we propagated it, leave the input alone
  // so half-typed text is not wiped.
  const lastPropagatedRef = useRef(value || '');

  useEffect(() => {
    const next = value || '';
    if (next !== lastPropagatedRef.current) {
      lastPropagatedRef.current = next;
      setManualUrl(next);
      setManualUrlError('');
    }
  }, [value]);

  const setBusy = busy => {
    setUploadLoading(busy);
    if (onBusyChange) onBusyChange(busy);
  };

  // If the field unmounts mid-upload (modal closed by other means), release
  // the parent's busy gate so Save cannot stay disabled.
  useEffect(() => {
    return () => {
      if (onBusyChange) onBusyChange(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFile = async file => {
    if (!isMp4(file)) {
      setUploadError('Please select an MP4 video (.mp4).');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Please use a video under 4.5MB (${TARGET_HINT}).`);
      return;
    }

    setBusy(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const videoUrl = await uploadShopVideo(file, token);
      lastPropagatedRef.current = videoUrl;
      setManualUrl(videoUrl);
      setManualUrlError('');
      onChange(videoUrl);
      setUploadSuccess(true);
    } catch (error) {
      console.error('Error uploading video:', error);
      setUploadError(error.message || 'Failed to upload video. Please try again.');
      setShowManualUrl(true);
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = async event => {
    const file = event.target.files[0];
    // Reset so picking the same file again re-triggers onChange.
    event.target.value = '';
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = e => {
    e.preventDefault();
    if (!uploadLoading) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadLoading) return;
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleRemove = () => {
    setUploadError('');
    setUploadSuccess(false);
    lastPropagatedRef.current = '';
    setManualUrl('');
    setManualUrlError('');
    onChange('');
  };

  // The modal's Save button sits outside this form, so the input's type="url"
  // never runs: only an empty value or a full http(s) URL reaches the form
  // state. Invalid text stays local with an error and the saved value is left
  // as it was, so a Save mid-edit cannot silently drop an existing video —
  // clearing the input (or Remove video) is the explicit way to remove it.
  const handleManualUrlChange = e => {
    const next = e.target.value;
    setManualUrl(next);
    const trimmed = next.trim();
    if (trimmed === '' || isHttpUrl(trimmed)) {
      setManualUrlError('');
      lastPropagatedRef.current = trimmed;
      onChange(trimmed);
    } else {
      setManualUrlError(
        'Enter a full video URL starting with http:// or https://. The saved video stays unchanged until this is valid.'
      );
    }
  };

  return (
    <div className="mb-4">
      {label && (
        <Form.Label className="text-muted fw-medium mb-2" style={{ fontSize: '0.875rem' }}>
          {label}
        </Form.Label>
      )}

      <div
        onClick={() => !uploadLoading && document.getElementById(id).click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: '2px dashed',
          borderColor: isDragging ? '#6366f1' : '#dee2e6',
          backgroundColor: isDragging ? 'rgba(99, 102, 241, 0.05)' : '#f8f9fa',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center',
          cursor: uploadLoading ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
        }}
      >
        {uploadLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.75rem',
            }}
          >
            <Spinner animation="border" role="status" style={{ color: '#6366f1' }}>
              <span className="visually-hidden">Uploading...</span>
            </Spinner>
          </div>
        )}
        <input
          type="file"
          id={id}
          accept=".mp4,video/mp4"
          onChange={handleFileUpload}
          disabled={uploadLoading}
          style={{ display: 'none' }}
        />
        <div style={{ color: '#6c757d' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginBottom: '0.5rem', opacity: 0.6 }}
          >
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
            <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
          </svg>
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Drag & drop video here</div>
          <div style={{ fontSize: '0.875rem' }}>or click to browse</div>
          <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.7 }}>
            Accepts .mp4 (max 4.5MB, {TARGET_HINT}). H.264, no audio, fast start. Plays muted on
            desktop only; phones and empty fields show the description image + text.
          </div>
        </div>
      </div>

      {uploadSuccess && !uploadLoading && (
        <div
          className="mt-2 d-flex align-items-center"
          style={{ color: '#22c55e', fontSize: '0.875rem' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginRight: '0.375rem' }}
          >
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
          </svg>
          Upload successful!
        </div>
      )}

      {uploadError && (
        <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
          {uploadError}
        </div>
      )}

      <div className="mt-3 d-flex gap-3">
        <button
          type="button"
          onClick={() => setShowManualUrl(!showManualUrl)}
          disabled={uploadLoading}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#6c757d',
            fontSize: '0.875rem',
            cursor: uploadLoading ? 'default' : 'pointer',
            textDecoration: 'underline',
          }}
        >
          {showManualUrl ? 'Hide URL input' : 'Having trouble? Enter URL manually'}
        </button>
        {value && !uploadLoading && (
          <button
            type="button"
            onClick={handleRemove}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#dc3545',
              fontSize: '0.875rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Remove video
          </button>
        )}
      </div>

      {showManualUrl && (
        <div className="mt-3">
          <Form.Control
            type="url"
            placeholder="Paste video URL here (.mp4)"
            value={manualUrl}
            onChange={handleManualUrlChange}
            isInvalid={!!manualUrlError}
            disabled={uploadLoading}
          />
          {manualUrlError && (
            <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
              {manualUrlError}
            </div>
          )}
        </div>
      )}

      {value && (
        <div className="mt-3 p-3 bg-light rounded-3 text-center">
          <video
            key={value}
            src={value}
            muted
            controls
            playsInline
            preload="metadata"
            className="rounded-3 shadow-sm"
            style={{ maxHeight: '250px', maxWidth: '100%' }}
          />
        </div>
      )}
    </div>
  );
};

export default VideoUploadField;
